/**
 * Authenticated API Client
 *
 * Centralized utility for making authenticated API requests.
 * Automatically includes Authorization header with the user's access token.
 */

import { getBrowserClient } from '@/lib/integrations/supabase/client';
import { queueRequest } from '@/lib/core/offline/sync-queue';
import { registerBackgroundSync, isBackgroundSyncSupported } from '@/lib/core/offline/background-sync';
import { getAdminContextHeaders } from '@/lib/features/swr/config';

export interface AuthenticatedFetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  /** If true, throws an error if user is not authenticated. Default: true */
  requireAuth?: boolean;
  /** If true, will queue the request for background sync if offline. Only applies to mutations. Default: true */
  enableOfflineQueue?: boolean;
}

export class AuthenticationError extends Error {
  constructor(message: string = 'User is not authenticated') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export class ApiError extends Error {
  status: number;
  statusText: string;
  data?: unknown;

  constructor(status: number, statusText: string, data?: unknown) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

function isQueueableRequest(url: string, method: string): boolean {
  const isMutation = ['POST', 'PUT', 'DELETE'].includes(method.toUpperCase());
  const isApiEndpoint = url.startsWith('/api/');
  return isMutation && isApiEndpoint;
}

function extractRequestMetadata(url: string, method: string): { type?: 'other' | 'customer' | 'booking' | 'payment'; optimisticId?: string } {
  const metadata: { type?: 'other' | 'customer' | 'booking' | 'payment'; optimisticId?: string } = {};

  if (url.includes('/bookings')) {
    metadata.type = 'booking';
  } else if (url.includes('/payments') || url.includes('/mark-paid')) {
    metadata.type = 'payment';
  } else if (url.includes('/customers')) {
    metadata.type = 'customer';
  } else {
    metadata.type = 'other';
  }

  if (method === 'POST') {
    metadata.optimisticId = crypto.randomUUID();
  }

  return metadata;
}

/**
 * Makes an authenticated fetch request.
 * Automatically retrieves the user's session and includes the Authorization header.
 */
export async function authenticatedFetch(
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const { requireAuth = true, enableOfflineQueue = true, headers = {}, ...fetchOptions } = options;
  const method = fetchOptions.method || 'GET';

  const supabase = getBrowserClient();
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (requireAuth && (!session || sessionError)) {
    throw new AuthenticationError('Please log in to continue');
  }

  const adminHeaders = getAdminContextHeaders();
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...adminHeaders,
    ...headers,
  };

  if (session?.access_token) {
    requestHeaders['Authorization'] = `Bearer ${session.access_token}`;
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers: requestHeaders,
      credentials: 'include',
    });

    if (!response.ok) {
      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        errorData = await response.text();
      }
      throw new ApiError(response.status, response.statusText, errorData);
    }

    return response;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      const shouldQueue = enableOfflineQueue && isQueueableRequest(url, method);

      if (shouldQueue && isBackgroundSyncSupported()) {
        try {
          const businessId = session?.user?.user_metadata?.business_id;

          const requestId = await queueRequest({
            url,
            method,
            body: fetchOptions.body as string || '',
            headers: requestHeaders,
            businessId,
            metadata: extractRequestMetadata(url, method),
          });

          await registerBackgroundSync();

          return new Response(
            JSON.stringify({
              success: true,
              queued: true,
              message: 'Request queued for sync when connection is restored',
              requestId,
            }),
            {
              status: 202,
              statusText: 'Accepted (Queued)',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
        } catch {
          // Fall through to throw original error
        }
      }
    }

    throw error;
  }
}

/**
 * Makes an authenticated fetch request and parses the JSON response.
 */
export async function authenticatedFetchJson<T = unknown>(
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<T> {
  const response = await authenticatedFetch(url, options);
  return response.json();
}

/**
 * Helper for making authenticated POST requests with JSON body.
 */
export async function authenticatedPost<T = unknown>(
  url: string,
  body: unknown,
  options: Omit<AuthenticatedFetchOptions, 'method' | 'body'> = {}
): Promise<T> {
  return authenticatedFetchJson<T>(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Helper for making authenticated PUT requests with JSON body.
 */
export async function authenticatedPut<T = unknown>(
  url: string,
  body: unknown,
  options: Omit<AuthenticatedFetchOptions, 'method' | 'body'> = {}
): Promise<T> {
  return authenticatedFetchJson<T>(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Helper for making authenticated DELETE requests.
 */
export async function authenticatedDelete<T = unknown>(
  url: string,
  options: Omit<AuthenticatedFetchOptions, 'method'> = {}
): Promise<T> {
  return authenticatedFetchJson<T>(url, {
    ...options,
    method: 'DELETE',
  });
}

/**
 * Helper for making authenticated GET requests.
 */
export async function authenticatedGet<T = unknown>(
  url: string,
  options: Omit<AuthenticatedFetchOptions, 'method'> = {}
): Promise<T> {
  return authenticatedFetchJson<T>(url, {
    ...options,
    method: 'GET',
  });
}
